import { useEffect, useState } from 'react'
import { Award, Calendar, ChevronRight, Users } from 'lucide-react'
import axios from 'axios'
import Modal from '../components/Modal'
import PageHeader from '../components/PageHeader'

interface Member {
  id: number
  firstName: string
  lastName: string
  dateOfBirth: string | null
  profileImage: string | null
  experienceYears: number
  beltLevel: string
  category: string | null
  isAdmin: boolean
  role: string
  age: number | null
}

interface BeltHistoryEntry {
  beltLevel: string
  date: string
  comment: string | null
}

const beltLabels: Record<string, string> = {
  BLANCHE: 'Blanche',
  JAUNE: 'Jaune',
  ORANGE: 'Orange',
  VERTE: 'Verte',
  BLEUE: 'Bleue',
  MARRON: 'Marron',
  NOIRE: 'Noire',
}

const beltClasses: Record<string, string> = {
  BLANCHE: 'belt-white',
  JAUNE: 'belt-yellow',
  ORANGE: 'belt-orange',
  VERTE: 'belt-green',
  BLEUE: 'belt-blue',
  MARRON: 'belt-brown',
  NOIRE: 'belt-black',
}

const categoryLabels: Record<string, string> = {
  MINI_POUSSIN: 'Mini-poussin',
  POUSSIN: 'Poussin',
  PUPILLE: 'Pupille',
  BENJAMIN: 'Benjamin',
  MINIME: 'Minime',
  CADET: 'Cadet',
  JUNIOR: 'Junior',
  SENIOR: 'Senior',
}

const Members = () => {
  const [members, setMembers] = useState<Member[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedMember, setSelectedMember] = useState<Member | null>(null)
  const [beltHistory, setBeltHistory] = useState<BeltHistoryEntry[]>([])
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [historyError, setHistoryError] = useState(false)

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const response = await axios.get<Member[]>('/api/users')
        setMembers(response.data)
      } catch (error) {
        console.error('Erreur lors du chargement des membres:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchMembers()
  }, [])

  useEffect(() => {
    if (!selectedMember) {
      setBeltHistory([])
      return
    }

    let active = true
    setBeltHistory([])
    setHistoryError(false)
    setLoadingHistory(true)
    axios.get<BeltHistoryEntry[]>(`/api/users/${selectedMember.id}/belt-history`)
      .then((response) => { if (active) setBeltHistory(response.data) })
      .catch(() => { if (active) setHistoryError(true) })
      .finally(() => { if (active) setLoadingHistory(false) })
    return () => { active = false }
  }, [selectedMember])

  const getBeltLabel = (beltLevel: string) => beltLabels[beltLevel] || beltLevel
  const getBeltClass = (beltLevel: string) => beltClasses[beltLevel] || 'belt-white'

  const getRoleBadges = (role: string, isAdmin: boolean) => (
    <>
      {isAdmin && <span className="role-chip role-admin">Administrateur</span>}
      {role === 'SENSEI' && <span className="role-chip role-sensei">Sensei</span>}
      {role === 'ADHERENT' && <span className="role-chip role-member">Adhérent</span>}
    </>
  )

  const getImageUrl = (imagePath: string | null) => {
    if (!imagePath) return undefined
    return imagePath
  }

  const closeModal = () => setSelectedMember(null)

  if (loading) {
    return (
      <div className="empty-state" role="status">
        <div className="mx-auto h-9 w-9 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600" />
        <p className="mt-4 text-sm font-semibold text-gray-600">Chargement des adhérents…</p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Notre collectif"
        title="Les membres du club"
        description="Découvrez les pratiquants et enseignants qui font vivre le dojo tout au long de la saison."
        icon={<Users />}
      />

      {members.length > 0 ? (
        <div className="grid gap-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {members.map((member) => (
            <button
              key={member.id}
              type="button"
              className="card member-card min-h-[292px]"
              onClick={() => setSelectedMember(member)}
              aria-label={`Voir le profil de ${member.firstName} ${member.lastName}`}
            >
              <span className="relative z-[1] flex h-full flex-col items-center text-center">
                {member.profileImage ? (
                  <img
                    className="profile-avatar !h-20 !w-20 !rounded-[24px]"
                    src={getImageUrl(member.profileImage)}
                    alt={`${member.firstName} ${member.lastName}`}
                  />
                ) : (
                  <span className="profile-avatar profile-avatar-fallback !h-20 !w-20 !rounded-[24px]" aria-hidden="true">
                    {member.firstName[0]}{member.lastName[0]}
                  </span>
                )}

                <span className="mt-4 min-w-0">
                  <span className="block text-lg font-extrabold leading-tight text-gray-900">
                    {member.firstName} {member.lastName}
                  </span>
                  <span className="mt-1 block text-xs font-semibold text-gray-500">
                    {member.experienceYears} an{member.experienceYears !== 1 ? 's' : ''} de pratique
                  </span>
                </span>

                <span className="mt-4 flex flex-wrap justify-center gap-1.5">
                  <span className={`belt-chip ${getBeltClass(member.beltLevel)}`}>
                    Ceinture {getBeltLabel(member.beltLevel).toLowerCase()}
                  </span>
                  {getRoleBadges(member.role, member.isAdmin)}
                </span>

                <span className="mt-auto flex w-full items-center justify-between border-t border-primary-900/10 pt-4">
                  <span className="text-xs font-semibold text-gray-500">Profil du dojo</span>
                  <span className="card-link-hint">Découvrir <ChevronRight aria-hidden="true" /></span>
                </span>
              </span>
            </button>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <Users className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-3 text-lg font-bold text-gray-900">Aucun membre</h3>
          <p className="mt-1 text-sm text-gray-500">Aucun membre n'est actuellement inscrit au club.</p>
        </div>
      )}

      {selectedMember && (
        <Modal
          isOpen
          onClose={closeModal}
          title="Détails de l’adhérent"
          eyebrow="Profil du dojo"
          size="sm"
          footer={<button type="button" className="btn-secondary" onClick={closeModal}>Fermer</button>}
        >
          <div className="space-y-5">
            <section className="profile-hero">
              {selectedMember.profileImage ? (
                <img
                  className="profile-avatar"
                  src={getImageUrl(selectedMember.profileImage)}
                  alt={`${selectedMember.firstName} ${selectedMember.lastName}`}
                />
              ) : (
                <div className="profile-avatar profile-avatar-fallback" aria-hidden="true">
                  {selectedMember.firstName[0]}{selectedMember.lastName[0]}
                </div>
              )}
              <div className="min-w-0">
                <h3 className="text-xl font-extrabold text-gray-900">
                  {selectedMember.firstName} {selectedMember.lastName}
                </h3>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {getRoleBadges(selectedMember.role, selectedMember.isAdmin)}
                </div>
              </div>
            </section>

            <section className="detail-grid" aria-label="Informations de l’adhérent">
              {selectedMember.age !== null && (
                <div className="detail-tile">
                  <span><Calendar aria-hidden="true" /> Âge</span>
                  <strong>{selectedMember.age} ans</strong>
                </div>
              )}
              <div className="detail-tile">
                <span><Users aria-hidden="true" /> Expérience</span>
                <strong>{selectedMember.experienceYears} an{selectedMember.experienceYears !== 1 ? 's' : ''}</strong>
              </div>
              {selectedMember.category && (
                <div className="detail-tile">
                  <span><Users aria-hidden="true" /> Catégorie</span>
                  <strong>{categoryLabels[selectedMember.category] || selectedMember.category}</strong>
                </div>
              )}
              <div className={`detail-tile ${selectedMember.category ? '' : 'sm:col-span-2'}`}>
                <span><Award aria-hidden="true" /> Grade actuel</span>
                <span className={`belt-chip ${getBeltClass(selectedMember.beltLevel)}`}>
                  Ceinture {getBeltLabel(selectedMember.beltLevel).toLowerCase()}
                </span>
              </div>
            </section>

            <section aria-labelledby="belt-history-title">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <p className="eyebrow !mb-1">Progression</p>
                  <h3 id="belt-history-title" className="text-lg font-extrabold text-gray-900">Historique des ceintures</h3>
                </div>
                <Award className="h-6 w-6 text-primary-600" aria-hidden="true" />
              </div>

              {loadingHistory ? (
                <div className="detail-tile flex items-center gap-3" role="status">
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600" />
                  <span className="text-sm font-semibold text-gray-500">Chargement de la progression…</span>
                </div>
              ) : historyError ? (
                <div className="detail-tile text-sm text-gray-500" role="alert">Impossible de charger l’historique des passages de grade. Veuillez réessayer.</div>
              ) : beltHistory.length === 0 ? (
                <div className="detail-tile text-sm text-gray-500">Aucun passage de ceinture enregistré.</div>
              ) : (
                <ul className="history-list">
                  {beltHistory.map((entry, index) => (
                    <li key={`${entry.date}-${entry.beltLevel}-${index}`} className="history-item flex-wrap">
                      <span className={`belt-chip ${getBeltClass(entry.beltLevel)}`}>
                        {getBeltLabel(entry.beltLevel)}
                      </span>
                      <span className="flex items-center gap-1.5 text-xs font-bold text-gray-500">
                        <Calendar className="h-4 w-4" aria-hidden="true" />
                        {new Date(entry.date).toLocaleDateString('fr-FR')}
                      </span>
                      {entry.comment?.trim() && (
                        <p className="w-full whitespace-pre-wrap break-words text-sm text-gray-600 [overflow-wrap:anywhere]">{entry.comment}</p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </Modal>
      )}
    </div>
  )
}

export default Members
